import axios from 'axios'
import api from '@/api'

const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]))
  } catch (e) {
    return null
  }
}

const state = {
  token: localStorage.getItem('user-token') || '',
  expire: localStorage.getItem('user-token-expiry') || '',
  user: localStorage.getItem('user-name') || '',
  name: localStorage.getItem('user-full-name') || '',
  id: localStorage.getItem('user-id') || '',
  admin: localStorage.getItem('user-admin') ? JSON.parse(localStorage.getItem('user-admin')) : false,
  su: localStorage.getItem('user-su') ? JSON.parse(localStorage.getItem('user-su')) : false,
  lastActivity: '',
  reset: false,
  domainInfo: {}
}

const getters = {
  loggedIn: state => !!state.token,
  reset: state => !!state.reset,
  token: state => state.token,
  parsedToken: state => parseJwt(state.token) || {},
  tokenExpires: (state, getters) => state.expire,
  lastActivity: state => state.lastActivity,
  isSuper: state => !!state.su,
  isAdmin: state => !!state.admin,
  getDomainInfo: state => state.domainInfo,
  userInfo: state => ({
    'username': state.user,
    'name': state.name,
    'id': state.id,
    'su': state.su,
    'admin': state.admin
  })
}

const mutations = {
  logIn (state, { user, name, id, admin, su, reset = false }) {
    state.user = user
    state.name = name
    state.id = id
    state.admin = admin
    state.su = su
    state.reset = reset
  },
  logOut (state) {
    state.token = ''
    state.user = ''
    state.name = ''
    state.id = ''
    state.admin = ''
    state.su = ''
    state.reset = ''
  },
  setToken (state, {token, expire}) {
    state.token = token
    state.expire = expire
  },
  setLastActivity (state, {lastActivity}) {
    state.lastActivity = lastActivity
  },
  setDomainInfo (state, domainInfo) {
    state.domainInfo = domainInfo
    if (!document.title.includes(domainInfo.app_name)) {
      document.title = document.title + " | " + domainInfo.app_name
    }
  }
}

const actions = {
  logIn ({ dispatch }, user) {
    return new Promise((resolve, reject) => {
      api.auth.auth.login(user)
        .then(({ token, expire, user, name, id, is_org_admin = false, su, reset = false }) => {
          dispatch('setSession', { token, expire, user, name, id, admin: is_org_admin, su, reset })
            .then(() => {
              resolve()
            })
        }, error => {
          reject(error)
        })
    })
  },
  resetPw ({ dispatch }, email) {
    return new Promise((resolve, reject) => {
      api.auth.auth.resetPw(email)
        .then(() => {
          resolve(true)
        }, error => {
          reject(error)
        })
    })
  },
  clientLogIn ({}, {email=null, phone=null, from=null}) {
    return new Promise((resolve, reject) => {
      api.auth.auth.magicAuth({email, phone, from})
        .then(() => {
          resolve()
        }, error => {
          reject(error)
        })
    })
  },
  magicLogIn ({ dispatch }, token) {
    return new Promise((resolve, reject) => {
      api.auth.auth.refreshToken(token)
        .then(({ token, expire, user, name, id, su }) => {
          dispatch('setSession', { token, expire, user, name, id, su })
            .then(() => {
              resolve()
            }, error => {
              reject(error)
            })
        }, error => {
          reject(error)
        })
    })
  },
  setToken ({ commit }, {token, expire, setStorage = true}) {
    if (setStorage) {
      localStorage.setItem('user-token', token)
      localStorage.setItem('user-token-expiry', expire)
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    commit('setToken', {token, expire})
  },
  setLastActivity ({ commit }) {
    var time_event = new Date()
    var epoch = (time_event.getTime() / 1000).toFixed(0)
    commit('setLastActivity', {lastActivity: epoch})
  },
  syncToken ({ dispatch, state }) {
    if (state.token !== localStorage.getItem('user-token')) {
      if (localStorage.getItem('user-token')) {
        var token = localStorage.getItem('user-token')
        var expire = localStorage.getItem('user-token-expiry')
        dispatch('setToken', {token, expire, setStorage: false})
        dispatch('setLastActivity')
      } else {
        dispatch('logOut')
      }
    }
  },
  setSession ({ commit, dispatch }, { token, expire, user, name, id, admin, su, reset, storage = false }) {
    return new Promise((resolve) => {
      // If setting the session from a "new" log in, i want to calculate the 15 minute expiry here:
      // But if reading from local storage, I don't want to calculate again (say, page refresh or new tab)
      // So if storage = true (reading from local storage) just set the token.
      // -- Otherwise, calcualte time and set.
      if (storage) {
        dispatch('setLastActivity')
        dispatch('setToken', {token, expire})
      } else {
        var expire_time = new Date()
        expire_time.setMinutes(expire_time.getMinutes() + (expire / 60))
        dispatch('setToken', {token, expire: expire_time.getTime()})
      }
      localStorage.setItem('user-name', user)
      localStorage.setItem('user-full-name', name)
      if (id) localStorage.setItem('user-id', id)
      if (admin) localStorage.setItem('user-admin', admin)
      if (su) localStorage.setItem('user-su', su)
      commit('logIn', { user, name, id, admin, su, reset })
      resolve()
    })
  },
  refreshToken ({ state, dispatch }) {
    return new Promise((resolve, reject) => {
      api.auth.auth.refreshToken(state.token)
        .then(({ token, expire, user, name, id, is_org_admin = false, su }) => {
          dispatch('setSession', { token, expire, user, name, id, admin: is_org_admin, su })
            .then(() => {
              resolve()
            })
        }, error => {
          reject(error)
        })
    })
  },
  logOut ({ commit }) {
    return new Promise(resolve => {
      commit('logOut')
      localStorage.removeItem('user-token')
      localStorage.removeItem('user-token-expiry')
      localStorage.removeItem('user-name')
      localStorage.removeItem('user-full-name')
      localStorage.removeItem('user-id')
      localStorage.removeItem('user-admin')
      localStorage.removeItem('user-su')
      delete axios.defaults.headers.common['Authorization']
      resolve()
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
